import type { AdminIdentity, TotpStatus } from "@player115/contracts";
import { Copy, KeyRound, LoaderCircle, ShieldCheck, ShieldOff } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { Status } from "../components/Status";

type Enrollment = { secret: string; qrCodeDataUrl: string };

export function SecurityPage({ admin, onAdminChange }: {
  admin: AdminIdentity;
  onAdminChange: (admin: AdminIdentity) => void;
}) {
  const [status, setStatus] = useState<TotpStatus | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.totpStatus().then(setStatus).catch((reason) => setError(reason instanceof Error ? reason.message : "读取安全设置失败"));
  }, []);

  const beginSetup = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      setEnrollment(await api.setupTotp(password));
      setPassword("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "无法开始绑定");
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api.confirmTotp(code);
      onAdminChange(result.admin);
      setStatus({ enabled: true, enabledAt: new Date().toISOString(), recoveryCodesRemaining: result.recoveryCodes.length });
      setRecoveryCodes(result.recoveryCodes);
      setEnrollment(null);
      setCode("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "验证码确认失败");
    } finally {
      setBusy(false);
    }
  };

  const disable = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api.disableTotp(password, code);
      onAdminChange(result.admin);
      setStatus({ enabled: false, enabledAt: null, recoveryCodesRemaining: 0 });
      setPassword("");
      setCode("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "无法关闭二次验证");
    } finally {
      setBusy(false);
    }
  };

  const copyRecoveryCodes = async () => {
    if (!recoveryCodes) return;
    await navigator.clipboard.writeText(recoveryCodes.join("\n"));
    setCopied(true);
  };

  return <section className="page">
    <header className="page-head"><div><h1>登录安全</h1><p>使用 Google Authenticator 保护管理员账号</p></div><Status tone={status?.enabled ? "success" : "warning"}>{status?.enabled ? "已启用" : "未启用"}</Status></header>
    {error && <div className="inline-error" role="alert"><span>{error}</span><button type="button" onClick={() => setError("")}>关闭</button></div>}
    {!status ? <div className="security-section page-state"><LoaderCircle className="spin" size={22} /><strong>正在读取安全设置</strong></div>
      : recoveryCodes ? <section className="security-section recovery-section">
        <header><ShieldCheck size={20} /><div><h2>保存恢复码</h2><p>每个恢复码只能使用一次。请保存到密码管理器，不要发送给他人。</p></div></header>
        <div className="recovery-grid">{recoveryCodes.map((item) => <code key={item}>{item}</code>)}</div>
        <div className="security-actions"><button className="secondary-button" type="button" onClick={() => void copyRecoveryCodes()}><Copy size={16} />{copied ? "已复制" : "复制恢复码"}</button><button className="primary-button" type="button" onClick={() => setRecoveryCodes(null)}>我已安全保存</button></div>
      </section>
        : status.enabled ? <section className="security-section">
          <header><ShieldCheck size={20} /><div><h2>Google 验证器已启用</h2><p>以后每次密码验证通过后，还需要输入手机上的 6 位动态验证码。</p></div></header>
          <dl className="security-facts"><div><dt>启用时间</dt><dd>{status.enabledAt ? new Date(status.enabledAt).toLocaleString("zh-CN") : "已启用"}</dd></div><div><dt>剩余恢复码</dt><dd>{status.recoveryCodesRemaining} 个</dd></div></dl>
          <form className="danger-zone" onSubmit={(event) => void disable(event)}>
            <div><ShieldOff size={18} /><span><strong>关闭二次验证</strong><small>关闭后，账号只需要密码即可登录。</small></span></div>
            <label><span>当前密码</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
            <label><span>动态验证码或恢复码</span><input autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value.trim())} /></label>
            <button className="danger-button" type="submit" disabled={busy || !password || code.length < 6}>{busy ? "正在关闭" : "确认关闭"}</button>
          </form>
        </section>
          : <section className="security-section">
            {!enrollment ? <>
              <header><ShieldOff size={20} /><div><h2>启用 Google 验证器</h2><p>绑定后，只有同时持有密码和你的手机才能登录管理后台。</p></div></header>
              <form className="security-form" onSubmit={(event) => void beginSetup(event)}>
                <label><span>确认当前密码</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="输入当前管理员密码" /></label>
                <button className="primary-button" type="submit" disabled={busy || !password}><KeyRound size={16} />{busy ? "正在验证" : "开始绑定"}</button>
              </form>
            </> : <>
              <header><ShieldCheck size={20} /><div><h2>扫描二维码</h2><p>在 Google Authenticator 中添加账号，然后输入应用生成的 6 位验证码完成绑定。</p></div></header>
              <div className="totp-enrollment">
                <div className="qr-code">{enrollment.qrCodeDataUrl ? <img src={enrollment.qrCodeDataUrl} alt="Google Authenticator 绑定二维码" /> : <span>演示模式不生成二维码</span>}</div>
                <div className="enrollment-steps"><ol><li>打开 Google Authenticator</li><li>点击“添加代码”并扫描二维码</li><li>输入新账号显示的验证码</li></ol><div><span>无法扫码时输入密钥</span><code>{enrollment.secret}</code></div></div>
              </div>
              <form className="confirm-code" onSubmit={(event) => void confirmSetup(event)}>
                <label><span>6 位动态验证码</span><input className="code-input" autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" /></label>
                <div><button className="secondary-button" type="button" onClick={() => { setEnrollment(null); setCode(""); }}>取消</button><button className="primary-button" type="submit" disabled={busy || code.length !== 6}>{busy ? "正在确认" : "确认并启用"}</button></div>
              </form>
            </>}
          </section>}
  </section>;
}
